import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Calendar, Clock, Users, Share2, ArrowLeft, ExternalLink } from "lucide-react";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../context/studentAuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const DEMO_YOUTUBE_EMBED_URL = "https://www.youtube.com/embed/dQw4w9WgXcQ";

const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";

  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "").trim();
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }

    if (parsed.hostname.includes("youtube.com")) {
      const id = parsed.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }

    return "";
  } catch (err) {
    return "";
  }
};

const GroupClassDetails = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { student: authStudent } = useAuth();

  const [groupClass, setGroupClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const [bookingMethod, setBookingMethod] = useState("payment");
  const [couponCode, setCouponCode] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);

  const student = useMemo(() => {
    if (authStudent?.id) return authStudent;
    try {
      const raw = localStorage.getItem("student");
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }, [authStudent]);

  const showNotice = (message, type = "info") => {
    setNotice({ message, type });
    setTimeout(() => {
      setNotice({ message: "", type: "" });
    }, 3500);
  };

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/students/group-classes/approved/${classId}`, {
        params: {
          studentId: student?.id || "",
        },
      });
      setGroupClass(res.data?.data || null);
    } catch (err) {
      console.error(err);
      showNotice(err?.response?.data?.message || "Failed to fetch class details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (classId) {
      fetchDetails();
    }
  }, [classId, student?.id]);

  useEffect(() => {
    const verifyPaymentFromRedirect = async () => {
      const params = new URLSearchParams(window.location.search);
      const reference = params.get("reference") || params.get("trxref");
      const payment = params.get("payment");
      const paidClassId = params.get("classId") || classId;

      if (!reference || payment !== "group-class" || !student?.id || !paidClassId) {
        return;
      }

      try {
        setBookingBusy(true);
        const verifyRes = await axios.post(`${BASE_URL}/api/students/group-classes/verify-payment`, {
          reference,
          classId: paidClassId,
          studentId: student.id,
        });

        if (verifyRes.data?.success) {
          showNotice("Payment verified. Seat booked successfully.", "success");
          await fetchDetails();
        } else {
          showNotice(verifyRes.data?.message || "Payment verification failed", "error");
        }
      } catch (err) {
        console.error(err);
        showNotice(err?.response?.data?.message || "Payment verification failed", "error");
      } finally {
        setBookingBusy(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    verifyPaymentFromRedirect();
  }, [classId, student?.id]);

  const handleShare = async () => {
    const shareUrl = window.location.href;

    try {
      await navigator.clipboard.writeText(shareUrl);
      showNotice("Class link copied. You can now share it.", "success");
    } catch (err) {
      showNotice("Unable to copy link. Please copy from your address bar.", "error");
    }
  };

  const handleBookWithCoupon = async () => {
    if (!student?.id) {
      showNotice("Please login as a student to continue.", "error");
      return;
    }

    if (!couponCode.trim()) {
      showNotice("Enter a coupon code.", "error");
      return;
    }

    try {
      setBookingBusy(true);
      const res = await axios.post(`${BASE_URL}/api/students/group-classes/book-with-coupon`, {
        classId,
        studentId: student.id,
        couponCode: couponCode.trim(),
      });

      if (res.data?.success) {
        showNotice("Seat booked successfully with coupon.", "success");
        setCouponCode("");
        await fetchDetails();
      } else {
        showNotice(res.data?.message || "Unable to book seat", "error");
      }
    } catch (err) {
      console.error(err);
      showNotice(err?.response?.data?.message || "Unable to book with coupon", "error");
    } finally {
      setBookingBusy(false);
    }
  };

  const handleBookWithPayment = async () => {
    if (!student?.id || !student?.email) {
      showNotice("Please login with a valid student account.", "error");
      return;
    }

    try {
      setBookingBusy(true);
      const initRes = await axios.post(`${BASE_URL}/api/students/group-classes/initialize-payment`, {
        classId,
        studentId: student.id,
        email: student.email,
      });

      const authorizationUrl = initRes.data?.authorization_url;

      if (!authorizationUrl) {
        setBookingBusy(false);
        showNotice(initRes.data?.message || "Unable to initialize payment", "error");
        return;
      }

      window.location.href = authorizationUrl;
    } catch (err) {
      console.error(err);
      setBookingBusy(false);
      showNotice(err?.response?.data?.message || "Unable to initialize payment", "error");
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const youtubeEmbedUrl = getYouTubeEmbedUrl(groupClass?.video_link);
  const previewEmbedUrl = groupClass?.video_link
    ? (youtubeEmbedUrl || DEMO_YOUTUBE_EMBED_URL)
    : "";
  const isEnrolled = Number(groupClass?.is_enrolled) === 1;
  const seatsBooked = Number(groupClass?.max_students || 0) - Number(groupClass?.seats_left || 0);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button
              type="button"
              onClick={() => navigate("/group-classes")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-600"
            >
              <ArrowLeft size={16} />
              Back to all group classes
            </button>
          </div>

          {notice.message && (
            <div
              className={`mb-5 rounded-lg px-4 py-3 text-sm ${
                notice.type === "error"
                  ? "bg-red-50 text-red-700 border border-red-100"
                  : notice.type === "success"
                    ? "bg-green-50 text-green-700 border border-green-100"
                    : "bg-blue-50 text-blue-700 border border-blue-100"
              }`}
            >
              {notice.message}
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">
              Loading class details...
            </div>
          ) : !groupClass ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">
              Class details unavailable.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <section className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 shadow-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h1 className="text-2xl font-extrabold text-slate-900">{groupClass.title}</h1>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold ${
                          groupClass.brand_classification === 'big_brand_partner'
                            ? 'bg-amber-300 text-amber-950'
                            : 'bg-slate-300 text-slate-900'
                        }`}
                      >
                        {groupClass.brand_classification === 'big_brand_partner'
                          ? 'Big Brand / Partner'
                          : 'Normal Tutor'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Tutor: {groupClass.tutor_name || "-"}</p>
                  </div>

                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <Share2 size={16} />
                    Share Class
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-slate-700">
                  <p className="inline-flex items-center gap-2">
                    <Calendar size={16} className="text-slate-400" />
                    {formatDateTime(groupClass.start_date)}
                  </p>
                  <p className="inline-flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    {formatDateTime(groupClass.end_date)}
                  </p>
                  <p className="inline-flex items-center gap-2">
                    <Users size={16} className="text-slate-400" />
                    {seatsBooked}/{groupClass.max_students} booked
                  </p>
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-bold text-slate-900">About This Class</h2>
                  <p className="text-slate-700 mt-2 whitespace-pre-line">
                    {groupClass.description || "No class description yet."}
                  </p>
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-bold text-slate-900">Course Preview</h2>
                  {previewEmbedUrl ? (
                    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 aspect-video">
                      <iframe
                        title="Group class video preview"
                        src={previewEmbedUrl}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">No preview video has been added yet.</p>
                  )}
                </div>

                <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-blue-900 mb-4">Protected Class Details</h3>
                  {isEnrolled ? (
                    <div className="space-y-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-white rounded-xl border border-blue-100 shadow-sm">
                        <div className="grow">
                            <p className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-1">Live Meeting</p>
                            <p className="text-sm font-medium text-slate-700 break-all">{groupClass.class_link}</p>
                        </div>
                        <a 
                            href={groupClass.class_link} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-lg"
                        >
                          <ExternalLink size={20} />
                          Join Live Class
                        </a>
                      </div>
                      
                      <div className="bg-white/50 p-4 rounded-xl border border-blue-50">
                        <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Curriculum</p>
                        <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                            {groupClass.curriculum || "No detailed curriculum provided yet."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-slate-600">
                      Enroll to view sensitive details like the live class link and full curriculum.
                    </p>
                  )}
                </div>
              </section>

              <aside className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 h-fit">
                <p className="text-sm text-slate-500">Class Fee</p>
                <p className="text-2xl font-extrabold text-slate-900 mt-1">
                  ₦{Number(groupClass.price || 0).toLocaleString()}
                </p>

                {isEnrolled ? (
                  <div className="mt-5 rounded-lg border border-green-100 bg-green-50 p-3 text-sm text-green-700">
                    You are enrolled in this class.
                  </div>
                ) : (
                  <>
                    <div className="mt-5 space-y-2">
                      <p className="text-sm font-semibold text-slate-800">Book your seat with:</p>

                      <button
                        type="button"
                        onClick={() => setBookingMethod("payment")}
                        className={`w-full rounded-lg px-4 py-2 text-sm font-semibold border ${
                          bookingMethod === "payment"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300"
                        }`}
                      >
                        Payment
                      </button>

                      <button
                        type="button"
                        onClick={() => setBookingMethod("coupon")}
                        className={`w-full rounded-lg px-4 py-2 text-sm font-semibold border ${
                          bookingMethod === "coupon"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300"
                        }`}
                      >
                        Coupon Code
                      </button>
                    </div>

                    {bookingMethod === "coupon" && (
                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-slate-800 mb-2">Enter Coupon Code</label>
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Class coupon"
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      {bookingMethod === "coupon" ? (
                        <button
                          type="button"
                          onClick={handleBookWithCoupon}
                          disabled={bookingBusy}
                          className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {bookingBusy ? "Booking..." : "Book Seat With Coupon"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleBookWithPayment}
                          disabled={bookingBusy}
                          className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {bookingBusy ? "Preparing Payment..." : "Book Seat With Payment"}
                        </button>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-5 text-sm text-slate-600">
                  <Link to="/group-classes" className="text-blue-600 font-semibold">
                    View other classes
                  </Link>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GroupClassDetails;
